import { useState } from 'react';

import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  LookbackOption,
  MixpanelEvent,
  QUOTE_STATUS_MAPPING,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  formattedDateForSubscription,
  tabLabelerEnumerated,
  useCallbackRef,
  useConstant,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type Column,
  type PersistedDefaultTabProps,
  type TabProps,
} from '@talos/kyoko';

import { QuotesV2BlotterTable } from './QuotesV2BlotterTable';
import { QUOTES_V2_BLOTTER_PREFIX } from './tokens';
import type { QuotesV2TableFilter } from './useQuotesV2Filter';

export interface QuoteTabProps extends TabProps {
  defaultFilter: QuotesV2TableFilter;
  defaultColumns: Partial<Column>[];
  showAllDecimals?: boolean;
  groupByInitiatingFrom?: boolean;
  groupByParentRFQID?: boolean;
}

const OPEN_QUOTES_TAB_ID = 'open-quotes';

const PRESET_TABS: PersistedDefaultTabProps<QuoteTabProps>[] = [
  {
    label: 'Open',
    id: OPEN_QUOTES_TAB_ID,
    closable: false,
    editable: false,
    defaultFilter: {
      _start: LookbackOption.PastWeek,
      _statuses: ['All Open'],
      Statuses: QUOTE_STATUS_MAPPING['All Open'],
    },
    defaultColumns: [],
  },
  {
    label: 'Recent',
    id: 'recent-quotes',
    closable: true,
    editable: true,
    defaultFilter: {
      _start: LookbackOption.PastWeek,
    },
    defaultColumns: [],
  },
];

const NEW_TAB_DEFAULTS: QuoteTabProps = {
  defaultFilter: {
    StartDate: formattedDateForSubscription(Sugar.Date.create('2 days ago')),
  },
  defaultColumns: [],
};

// Lock the open tab, same as we do for Open Orders
const EXCLUDED_TABS = [OPEN_QUOTES_TAB_ID];

const tabLabeler = tabLabelerEnumerated('RFQs');

export function QuotesV2() {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);
  const handleSelect = useCallbackRef(() => setInitialFiltersOpen(false));
  const handleAdd = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  });

  const defaultInitialItems = useConstant<PersistedDefaultTabProps<QuoteTabProps>[]>(PRESET_TABS);

  const persistedTabs = usePersistedTabs<QuoteTabProps>(QUOTES_V2_BLOTTER_PREFIX, {
    defaultInitialItems,
    defaultInitialSelectedIndex: 0,
    excludedIDs: EXCLUDED_TABS,
    onSelect: handleSelect,
  });

  const tabs = useTabs<QuoteTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
  });

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large}>
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={true} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map(tab => (
          <AccordionGroup key={tab.id}>
            <QuotesV2BlotterTable
              blotterID={`${QUOTES_V2_BLOTTER_PREFIX}/${tab.id}`}
              // For the Open Quotes tab, we "lock" in the pre-applied All Open filter clause
              showStatusFilter={tab.id !== OPEN_QUOTES_TAB_ID}
              defaultFilter={tab.defaultFilter ?? NEW_TAB_DEFAULTS.defaultFilter}
              initialIsOpen={initialFiltersOpen}
              settings={{
                showAllDecimals: tab.showAllDecimals,
                groupByInitiatingFrom: tab.groupByInitiatingFrom,
                groupByParentRFQID: tab.groupByParentRFQID,
              }}
              onSettingsChanged={changedSettings => tabs.updateItem({ ...tab, ...changedSettings })}
            />
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
}
